import React, { useMemo } from 'react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { useDisclosure } from '../../hooks/useDisclosure';
import { cn } from '../ui/utils';

const kpiConfig = [
  {
    key: 'totalScore',
    label: 'Score',
    image: '/assets/images/result-score.svg',
    format: (value) => (value === '-' ? '-' : `${value} points`),
    cardClass: 'border-violet-200 bg-violet-50',
    labelClass: 'text-violet-700',
    valueClass: 'text-violet-900'
  },
  {
    key: 'answered',
    label: 'Solved',
    image: '/assets/images/result-solved.svg',
    format: (value) => (value === '-' ? '-' : `${value} questions`),
    cardClass: 'border-[#C0E281]/60 bg-[#C0E281]/20',
    labelClass: 'text-green-800',
    valueClass: 'text-green-900'
  },
  {
    key: 'accuracy',
    label: 'Accuracy',
    image: '/assets/images/result-accuracy.svg',
    format: (value) => (value === '-' ? '-' : `${value}%`),
    getCardStyle: (value) => {
      const num = Number(value);
      if (value === '-' || Number.isNaN(num)) {
        return {
          cardClass: 'border-primary/20 bg-primary/5',
          labelClass: 'text-primary',
          valueClass: 'text-foreground'
        };
      }
      if (num >= 80) {
        return {
          cardClass: 'border-emerald-200 bg-emerald-50',
          labelClass: 'text-emerald-700',
          valueClass: 'text-emerald-900'
        };
      }
      if (num >= 60) {
        return {
          cardClass: 'border-sky-200 bg-sky-50',
          labelClass: 'text-sky-700',
          valueClass: 'text-sky-900'
        };
      }
      if (num >= 40) {
        return {
          cardClass: 'border-amber-200 bg-amber-50',
          labelClass: 'text-amber-700',
          valueClass: 'text-amber-900'
        };
      }
      return {
        cardClass: 'border-rose-200 bg-rose-50',
        labelClass: 'text-rose-700',
        valueClass: 'text-rose-900'
      };
    }
  },
  {
    key: 'timeTaken',
    label: 'Speed (mins)',
    image: '/assets/images/result-speed.svg',
    format: (value) => (value === '-' ? '-' : `${value}`),
    cardClass: 'border-orange-200 bg-orange-50',
    labelClass: 'text-orange-700',
    valueClass: 'text-orange-900'
  }
];

function getPerformanceTheme(accuracy) {
  const num = Number(accuracy);

  if (accuracy === '-' || Number.isNaN(num)) {
    return {
      title: 'Good work!',
      subtitle: "Here's how you performed on this assignment",
      headerClass: 'border-primary/15 bg-gradient-to-br from-primary/10 via-white to-violet-50',
      badgeClass: 'bg-primary/15 text-primary'
    };
  }

  if (num >= 80) {
    return {
      title: 'Excellent work!',
      subtitle: 'Outstanding performance — you nailed it!',
      headerClass: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-[#C0E281]/20',
      badgeClass: 'bg-[#C0E281]/40 text-green-800'
    };
  }

  if (num >= 60) {
    return {
      title: 'Good work!',
      subtitle: 'Solid effort — review answers to improve further',
      headerClass: 'border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-primary/5',
      badgeClass: 'bg-sky-100 text-sky-800'
    };
  }

  if (num >= 40) {
    return {
      title: 'Keep going!',
      subtitle: 'You made progress — practice will help you improve',
      headerClass: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50',
      badgeClass: 'bg-amber-100 text-amber-800'
    };
  }

  return {
    title: 'Keep practicing!',
    subtitle: 'Review your answers and try again next time',
    headerClass: 'border-rose-200/80 bg-gradient-to-br from-rose-50 via-white to-orange-50',
    badgeClass: 'bg-rose-100 text-rose-800'
  };
}

const AssignmentResult = (props) => {
  const { buttonText, result, reviewFirstQuestion, revealSolutions, onGoHome, defaultOpen = false } =
    props;
  const { isOpen, onOpen, onClose } = useDisclosure(defaultOpen);
  const accuracy = result?.accuracy ?? '-';
  const performanceTheme = useMemo(() => getPerformanceTheme(accuracy), [accuracy]);

  const resultKpis = useMemo(() => {
    const data = result || {};

    return kpiConfig.map((kpi) => {
      const rawValue = data[kpi.key] ?? '-';
      const styles = kpi.getCardStyle?.(rawValue) ?? {
        cardClass: kpi.cardClass,
        labelClass: kpi.labelClass,
        valueClass: kpi.valueClass
      };

      return {
        label: kpi.label,
        image: kpi.image,
        display: kpi.format(rawValue),
        ...styles
      };
    });
  }, [result]);

  return (
    <div className="flex flex-col items-center">
      <Dialog open={isOpen} onOpenChange={(open) => (open ? onOpen() : onClose())}>
        <DialogContent
          showCloseButton
          className="max-w-md gap-0 overflow-hidden rounded-2xl border-border/60 p-0"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <div
            className={cn(
              'border-b px-6 pb-5 pt-8 text-center',
              performanceTheme.headerClass
            )}
          >
            <img
              src="/assets/images/result-tick.svg"
              alt=""
              className="mx-auto mb-4 h-20 w-auto object-contain"
              aria-hidden
            />
            <DialogHeader className="items-center space-y-2 text-center">
              <span
                className={cn(
                  'inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide',
                  performanceTheme.badgeClass
                )}
              >
                {accuracy === '-' ? 'Results' : `${accuracy}% accuracy`}
              </span>
              <DialogTitle className="text-xl font-bold text-foreground">
                {performanceTheme.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {performanceTheme.subtitle}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {resultKpis.map((kpi) => (
                <div
                  key={kpi.label}
                  className={cn('flex items-center gap-3 rounded-xl border p-4', kpi.cardClass)}
                >
                  <img
                    src={kpi.image}
                    alt=""
                    className="h-12 w-12 shrink-0 object-contain"
                    aria-hidden
                  />
                  <div className="min-w-0 text-left">
                    <p className={cn('text-xs font-medium', kpi.labelClass)}>{kpi.label}</p>
                    <p className={cn('mt-0.5 text-sm font-semibold', kpi.valueClass)}>
                      {kpi.display}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {revealSolutions && (
              <div className="mt-6 flex flex-col gap-3">
                <Button
                  type="button"
                  className="h-11 w-full font-semibold"
                  onClick={reviewFirstQuestion}
                >
                  Review Answers
                </Button>
                {onGoHome ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-full font-semibold"
                    onClick={onGoHome}
                  >
                    Go to Home
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Button type="button" className="min-w-[200px] font-semibold" onClick={onOpen}>
        {buttonText}
      </Button>
    </div>
  );
};

export default AssignmentResult;
